import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePagination } from "../../../../hooks/usePagination";
import api from "../../../../util/api";
import {
  OrderApiType,
  PaginatedResponse,
  QueryKeys,
} from "../../../../util/types";

export interface OrdersFilters {
  amountFrom?: string;
  amountTo?: string;
  from?: string;
  to?: string;
  categories?: string | null;
  status?: string | null;
  source?: string | null;
  searchKeyword?: string | null;
  createdBy?: string;
  customer?: string | null;
  totalFrom?: string;
  totalTo?: string | null;
  paymentStatus?: string | null;
  paymentProvider?: string | null;
  showDeleted?: string | null;
  currency?: string;
}

export const useGetOrders = (
  filters: OrdersFilters = {},
  sort = { field: "createdAt", direction: "DESC" },
  ownerId?: string
) => {
  const { page, limit, onPaginationChange, resetPageNum } = usePagination();

  const {
    data,
    isLoading,
    isFetching,
    refetch: refreshOrders,
  } = useQuery<PaginatedResponse<OrderApiType>>({
    queryKey: [
      QueryKeys.LIST_ORDERS,
      page,
      limit,
      filters.searchKeyword ?? "",
      filters.amountFrom ?? "",
      filters.amountTo ?? "",
      filters.from ?? "",
      filters.to ?? "",
      filters.categories ?? "",
      filters.status ?? "",
      filters.source ?? "",
      filters.createdBy ?? "",
      filters.customer ?? "",
      filters.totalFrom ?? "",
      filters.totalTo ?? "",
      filters.paymentStatus ?? "",
      filters.paymentProvider ?? "",
      filters.showDeleted ?? "",
      filters.currency ?? "",
      sort.field,
      sort.direction,
      ownerId ?? "",
    ],
    queryFn: async () => {
      return await api.getOrders(
        page,
        limit,
        {
          sort: sort.field,
          sortDirection: sort.direction,
          ...filters,
        },
        ownerId
      );
    },
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    resetPageNum();
  }, [JSON.stringify(filters), ownerId]);

  // Reset page if current page is beyond available pages after filtering
  useEffect(() => {
    if (data?.totalDocs !== undefined && page > 1) {
      const maxPage = Math.ceil(data.totalDocs / limit);
      if (maxPage > 0 && page > maxPage) {
        resetPageNum();
      }
    }
  }, [data?.totalDocs, page, limit, resetPageNum]);

  return {
    orders: data?.docs ?? [],
    totalDocs: data?.totalDocs ?? 0,
    loading: isLoading || isFetching,
    refreshOrders,
    page,
    limit,
    onPaginationChange,
  };
};
